import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { toast } = useToast();

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast({
        title: "Welcome Back!",
        description: "You have successfully logged in.",
      });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both passwords match.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      const result = await register(registerData.name, registerData.email, registerData.password);
      if (result.isPending) {
        setRegistrationSuccess(true);
        toast({
          title: "Registration Submitted!",
          description: result.message,
        });
      }
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-black text-white pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-[#F4B400] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="text-black" size={40} />
            </div>
            <h1 className="text-3xl font-bold mb-4">Registration Pending</h1>
            <p className="text-gray-400 mb-8">
              Your account has been created and is awaiting approval by an administrator. 
              You will be notified when your account is activated.
            </p>
            <Card className="bg-white/10 border-[#F4B400]/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 text-left">
                  <CheckCircle className="text-[#F4B400] flex-shrink-0" size={24} />
                  <div>
                    <p className="font-semibold text-white">What happens next?</p>
                    <p className="text-sm text-gray-400">
                      An administrator will review your registration and assign the tools you can access.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Button
              onClick={() => setRegistrationSuccess(false)}
              variant="outline"
              className="mt-6 border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div className="container mx-auto px-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_enrigo-creative/artifacts/19zqfisl_EA%20Logo.png" 
              alt="Enrigo" 
              className="h-20 w-auto mx-auto mb-6"
            />
            <h1 className="text-4xl font-bold mb-2">Welcome to <span className="text-[#F4B400]">Enrigo</span></h1>
            <p className="text-gray-400">Access your dashboard and tools</p>
          </div>

          <Card className="bg-white text-black">
            <CardContent className="p-6">
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="login-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00] transition-all duration-300"
                      data-testid="login-submit-btn"
                    >
                      <LogIn className="mr-2" size={18} />
                      {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                    <div className="text-center mt-4">
                      <button
                        type="button"
                        onClick={() => navigate('/contact', { state: { preselectedService: 'Password Reset Request' } })}
                        className="text-sm text-gray-500 hover:text-[#F4B400] transition-colors"
                      >
                        Forgot Password? Contact Admin
                      </button>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-yellow-800">
                        <Clock className="inline mr-1" size={14} />
                        New accounts require admin approval before you can log in.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="register-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="register-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="register-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-confirm-password">Confirm Password</Label>
                      <Input
                        id="register-confirm-password"
                        type="password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00] transition-all duration-300"
                      data-testid="register-submit-btn"
                    >
                      <UserPlus className="mr-2" size={18} />
                      {isLoading ? 'Submitting...' : 'Submit Registration'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <p className="text-center text-gray-400 text-sm mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
